import { useMemo } from 'react'

import {
    AddBrandKitType,
    BrandKitType,
    DeleteBrandKitType,
    EditBrandKitType,
    SetActiveBrandKitType
} from 'app/types'

import { useAuth, useDataMutation, useQuery } from 'hooks'

const requestPath = '/c/v1/brand-kit'

export type BrandKitResponseType =
    | {
          message: 'string'
      }
    | undefined

export const useBrandKit = () => {
    const { orgId } = useAuth()

    const $brandKit = useQuery<BrandKitType>(`${requestPath}/get?orgId=${orgId}`, {
        method: 'GET'
    })

    const { mutate, isValidating, data } = $brandKit

    const callBack = useMemo(() => {
        return {
            onSuccess: () => mutate()
        }
    }, [mutate])

    const $editBrandKit = useDataMutation<EditBrandKitType, BrandKitResponseType, Error>(
        `${requestPath}/edit`,
        'POST',
        callBack
    )

    const $deleteFromBrandKit = useDataMutation<DeleteBrandKitType, BrandKitResponseType, Error>(
        `${requestPath}/delete`,
        'POST',
        callBack
    )

    const $addToBrandKit = useDataMutation<AddBrandKitType, BrandKitResponseType, Error>(
        `${requestPath}/add`,
        'POST',
        callBack
    )

    const $setActiveBrandKit = useDataMutation<SetActiveBrandKitType, BrandKitResponseType, Error>(
        `${requestPath}/set-active`,
        'POST',
        callBack
    )

    return useMemo(() => {
        const { color, font, qgHighlightColor, defaultKit, background, logoAndFavicon } = data || {}

        return {
            revalidateBrandKit: mutate,
            $editBrandKit,
            $deleteFromBrandKit,
            $addToBrandKit,
            $setActiveBrandKit,
            brandKitLoading: isValidating,
            brandKitData: data,
            logoAndFavicon:
                logoAndFavicon?.data?.find(({ id }) => id === logoAndFavicon.active)?.url || '',
            brandKitColors: color?.data.map(item => item.value) || [],
            brandKitBackgrounds:
                background?.data?.map(item => ({
                    ...item,
                    isActive: item.id === background?.active
                })) || [],
            brandKitActiveFont: font?.data?.find(({ id }) => id === font.active),
            brandKitHighlightColor:
                qgHighlightColor?.data?.find(it => it.id === qgHighlightColor?.active)?.value || '',
            brandKitActiveColor:
                color?.data?.find(({ id }) => id === color.active)?.value || defaultKit?.color || ''
        }
    }, [
        mutate,
        $editBrandKit,
        $deleteFromBrandKit,
        $addToBrandKit,
        $setActiveBrandKit,
        isValidating,
        data
    ])
}
